<template>
  <div class="bet-content">
    <div class="bet-content__item m-2 p-2" v-for="(item, index) in betslips" :key="index">
      <div class="header">
        <img class="logo" src="@/public/img/icon-horse.png" v-if="$store.state.settings.currentGame == 'hr'" />
        <img class="logo" src="@/public/img/icon-dog.png" v-else />
        <i class="bi bi-trash trash-icon" @click="$emit('remove', item)"></i>
        {{ getTranslatedText(item.marketTypeName) }}
      </div>

      <div class="row" v-if="item.horseName">
        <div class="col-10">
          <b :class="{ 'text-invalid': isBetInvalid(item) }">
            {{ getSelectionBetLabel(item) }}</b>
          &nbsp;
          <i v-if="isBetInvalid(item) && currentTab === 'SINGLE'"
            class="bi bi-x-lg" :class="{ 'text-invalid': isBetInvalid(item) }"></i>
        </div>
        <div class="col-2 d-flex justify-content-end"
          :class="{ 'text__increase': item.isIncrease, 'text__decrease': item.isIncrease == false }">
          <b>{{ item.odds }}</b>
          <i v-if="item.isIncrease != null"
            :class="item.isIncrease ? 'bi bi-arrow-up-short' : 'bi bi-arrow-down-short'"></i>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          {{ item.countryName }}, - {{ item.raceName }}
        </div>
      </div>

      <div class="row">
        <div class="col-6 d-flex align-items-center">
          {{ showDate(item.raceTime) }}
        </div>
        <div class="col-6 stake-col" v-if="currentTab === 'SINGLE'">
          <div class="stake-row">
            <b class="text-mobile stake-currency">{{ currencySymbol }}</b>
            <div class="amount-field-wrap">
              <input
                v-if="editingAmount[item.id]"
                type="text"
                inputmode="decimal"
                class="amount_input amount-field"
                :value="tempAmount[item.id]"
                @keydown="validateStakeInput"
                @input="$emit('update-temp-amount', item.id, $event.target.value)"
                @blur="$emit('save-amount', item)"
                @keyup.enter="$emit('save-amount', item)"
                :id="`amount-input-${item.id}`"
              />
              <div
                v-else
                @click="$emit('start-edit', item)"
                class="amount-display amount-field"
              >
                {{ formatStake(item.amount) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="item.isEW && item.runners >= 5 && currentTab === 'SINGLE'">
        <div class="col-12 d-flex justify-content-end align-items-center">
          <label>
            {{ getTranslatedText('E/W') }}
            <span v-if="item.eachWayBet">
              {{ item.eachWayFraction }} | {{ generateString(item.eachWayPlaces) }}
            </span>
          </label>
          &nbsp;
          <input :checked="item.eachWayBet" type="checkbox" class="checkbox-common"
            @change="(e) => $emit('change-each-way', e, item)">
        </div>
      </div>

      <template v-if="item.listHorse?.length > 0">
        <p v-for="(h, id) in item.listHorse" :key="id"
          :class="{ 'text-invalid': invalidBets.some((x) => x == h.fieldId || x == item.raceId) }">
          <b>{{ generateOrdinalNumber(h.position) }} - {{ h.horseName }}</b>
        </p>
      </template>

      <template v-if="currentTab === 'SINGLE'">
        <BetslipWinningsWithTax :item="item" v-if="wagerTax > 0"/>
        <BetslipWinnings :item="item" v-else/>
      </template>
    </div>
  </div>
</template>

<script>
import languageMixins from "@/mixins/language/language-mixins";
import utilsMixins from "@/mixins/utils/utils-mixins";
import { formatDateTimeZone, compareDates, fixedNumber } from "@/utils/utils";
import BetslipWinnings from './BetslipWinnings.vue';
import BetslipWinningsWithTax from './BetslipWinningsWithTax.vue';

export default {
  name: 'BetslipContent',
  mixins: [languageMixins, utilsMixins],
  components: { BetslipWinnings, BetslipWinningsWithTax },
  props: {
    betslips: {
      type: Array,
      required: true
    },
    currentTab: {
      type: String,
      required: true
    },
    invalidBets: {
      type: Array,
      required: true
    },
    editingAmount: {
      type: Object,
      required: true
    },
    tempAmount: {
      type: Object,
      required: true
    }
  },
  computed: {
    betMin() {
      return this.$store.state.settings.betMin;
    },
    betMax() {
      return this.$store.state.settings.betMax;
    },
    currencySymbol() {
      return this.$store.state.settings.currencySymbol;
    },
    wagerTax() {
      return this.$store.state.settings.wagerTax;
    },
  },
  watch: {
    editingAmount: {
      handler(newVal) {
        this.$nextTick(() => {
          const itemId = Object.keys(newVal).find(key => newVal[key]);
          if (itemId) {
            const input = document.getElementById(`amount-input-${itemId}`);
            if (input) input.focus();
          }
        });
      },
      deep: true
    }
  },
  methods: {
    isBetInvalid(item) {
      return this.invalidBets.some((x) =>
        x == item.fieldId || x == item.raceId || x == item.selectionId
      );
    },

    getSelectionBetLabel(item) {
      if (!item.isSelectionBet) return item.horseName;

      const marketName = this.getTranslatedText(item.marketName || item.marketTypeName);
      const selectionName = item.selectionName || item.horseName;

      if (marketName && selectionName) {
        return `${marketName} - ${selectionName}`;
      }

      return marketName || selectionName;
    },

    formatStake(amount) {
      return fixedNumber(amount);
    },

    showDate(date) {
      let d = formatDateTimeZone(date, this.$store.state.settings.timeZone)
      return compareDates(date, Date.now(), this.$store.state.settings.timeZone) == 0 ?
        `${this.getTranslatedText('today')} ${d.hour}:${d.minutes}` : `${d.day}-${d.month}-${d.year} ${d.hour}:${d.minutes}`;
    }
  }
};
</script>
